import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col, FormGroup, Label, Input } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { Edit } from 'react-feather';
import DataTable from '../../../Common/DataTable';
import { Btn } from '../../../../AbstractElements';
import { fetchClosedAccidentFiles } from '../../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import { normalizeDigitsOnly } from '../../../../_helper/regexHandler';

const TodoTabClosedFiles = ({ onCountChange, onLoadingChange }) => {
    const navigate = useNavigate();
    const isInitialLoad = useRef(true);
    const searchRef = useRef({
        applicationDateSearch: '',
        accidentDateSearch: '',
        tcIdSearch: '',
        closeCauseSearch: ''
    });

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [applicationDateSearch, setApplicationDateSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');
    const [tcIdSearch, setTcIdSearch] = useState('');
    const [closeCauseSearch, setCloseCauseSearch] = useState('');

    useEffect(() => {
        if (typeof onCountChange === 'function') {
            onCountChange(totalRows);
        }
    }, [onCountChange, totalRows]);

    useEffect(() => {
        if (typeof onLoadingChange === 'function') {
            onLoadingChange(loading);
        }
    }, [loading, onLoadingChange]);

    useEffect(() => {
        searchRef.current = {
            applicationDateSearch,
            accidentDateSearch,
            tcIdSearch,
            closeCauseSearch
        };
    }, [applicationDateSearch, accidentDateSearch, tcIdSearch, closeCauseSearch]);

    const fetchClosedFilesData = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/accidentfile';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();

            if (applicationDateSearch && applicationDateSearch.trim().length > 0) {
                queryParams.append('applicationDate', applicationDateSearch);
            }

            if (accidentDateSearch && accidentDateSearch.trim().length > 0) {
                queryParams.append('accidentDate', accidentDateSearch);
            }

            if (tcIdSearch && tcIdSearch.trim().length >= 3) {
                queryParams.append('tcId', tcIdSearch.trim());
            }

            if (closeCauseSearch && closeCauseSearch.trim().length > 0) {
                queryParams.append('fileCloseCause', closeCauseSearch.trim());
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            await fetchClosedAccidentFiles({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setItems,
                setTotalRowsCallback: setTotalRows,
            });
        } catch (error) {
            toast.error('Kapalı dosyalar alınırken bir hata oluştu.');
            setItems([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, applicationDateSearch, accidentDateSearch, tcIdSearch, closeCauseSearch]);

    const fetchClosedFilesDataNonSearch = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/accidentfile';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            const {
                applicationDateSearch: currentApplicationDateSearch,
                accidentDateSearch: currentAccidentDateSearch,
                tcIdSearch: currentTcIdSearch,
                closeCauseSearch: currentCloseCauseSearch
            } = searchRef.current;

            if (currentApplicationDateSearch && currentApplicationDateSearch.trim().length > 0) {
                queryParams.append('applicationDate', currentApplicationDateSearch);
            }

            if (currentAccidentDateSearch && currentAccidentDateSearch.trim().length > 0) {
                queryParams.append('accidentDate', currentAccidentDateSearch);
            }

            if (currentTcIdSearch && currentTcIdSearch.trim().length >= 3) {
                queryParams.append('tcId', currentTcIdSearch.trim());
            }

            if (currentCloseCauseSearch && currentCloseCauseSearch.trim().length > 0) {
                queryParams.append('fileCloseCause', currentCloseCauseSearch.trim());
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            await fetchClosedAccidentFiles({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setItems,
                setTotalRowsCallback: setTotalRows,
            });
        } catch (error) {
            toast.error('Kapalı dosyalar alınırken bir hata oluştu.');
            setItems([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchClosedFilesDataNonSearch();
    }, [fetchClosedFilesDataNonSearch, page, pageSize]);

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        const hasAnySearch = applicationDateSearch.trim()
            || accidentDateSearch.trim()
            || tcIdSearch.trim()
            || closeCauseSearch.trim();

        const timeoutId = setTimeout(() => {
            const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length > 0;
            const hasValidTcIdSearch = tcIdSearch && tcIdSearch.trim().length >= 3;

            if (hasAnySearch) {
                if (hasTcIdSearch && !hasValidTcIdSearch) {
                    return;
                }
                fetchClosedFilesData();
            } else {
                fetchClosedFilesDataNonSearch();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [applicationDateSearch, accidentDateSearch, tcIdSearch, closeCauseSearch, fetchClosedFilesData, fetchClosedFilesDataNonSearch]);

    const handleApplicationDateChange = (event) => {
        setApplicationDateSearch(event.target.value);
        setPage(1);
    };

    const handleAccidentDateChange = (event) => {
        setAccidentDateSearch(event.target.value);
        setPage(1);
    };

    const handleTcIdChange = (event) => {
        const inputValue = event.target.value;
        const numericValue = normalizeDigitsOnly(inputValue);
        setTcIdSearch(numericValue);
        setPage(1);
    };

    const handleCloseCauseChange = (event) => {
        setCloseCauseSearch(event.target.value);
        setPage(1);
    };

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (newPerPage, newPage) => {
        setPageSize(newPerPage);
        setPage(newPage);
    };

    const columns = useMemo(() => ([
        {
            name: 'Müşteri',
            selector: row => {
                const customer = row.customer;
                if (!customer) return '-';

                if (customer.customerType === 'Kurumsal') {
                    return customer.companyName || '-';
                }
                return customer.clientName || '-';
            },
            sortable: true,
            grow: 2,
            minWidth: '200px'
        },
        {
            name: 'TC/Vergi No',
            selector: row => {
                const customer = row.customer;
                if (!customer) return '-';

                if (customer.customerType === 'Kurumsal') {
                    return customer.taxNumber || '-';
                }
                return customer.identityNumber || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Dosyası No',
            selector: row => row.fileNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Plaka',
            selector: row => row.accident?.licensePlate?.licensePlateNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Başvuru Tarihi',
            selector: row => row.insuranceApplicationDate
                ? new Date(row.insuranceApplicationDate).toLocaleDateString('tr-TR')
                : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => row.accident?.accidentDate
                ? new Date(row.accident.accidentDate).toLocaleDateString('tr-TR')
                : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Talep Edilen İş',
            selector: row => {
                const filesToBeWorked = row.filesToBeWorked || [];
                if (filesToBeWorked.length === 0) return '-';

                const fileTypes = filesToBeWorked
                    .map(file => {
                        const fileTypeArray = file.fileType || [];
                        return fileTypeArray.map(ft => ft.title || '').filter(title => title !== '');
                    })
                    .flat()
                    .join(', ');

                return fileTypes || '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Çalışılan Tutar',
            selector: row => {
                const amountWorked = row.amountWorked;
                if (amountWorked) return `${amountWorked.toLocaleString('tr-TR')} ₺`;
                const filesToBeWorked = row.filesToBeWorked || [];
                const total = filesToBeWorked.reduce((sum, file) => sum + (Number(file?.fileExpense) || 0), 0);
                return total > 0 ? `${total.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Durum',
            selector: row => row.fileStatus || 'Kapalı',
            sortable: true,
            grow: 0.8,
            minWidth: '80px'
        },
        {
            name: 'Kapanma Açıklaması',
            selector: row => row.fileCloseDescription || '-',
            sortable: true,
            grow: 2,
            minWidth: '150px'
        },
        {
            name: 'Tahkim Detay',
            cell: (row) => {
                if (row.arbitration && row.arbitration.length > 0) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'primary',
                                size: 'sm',
                                outline: true,
                                onClick: () => navigate(`${process.env.PUBLIC_URL}/arbitration/${row.arbitration[0]}`),
                                title: 'Tahkim Detaylarını Görüntüle'
                            }}
                        >
                            <Edit size={18} />
                        </Btn>
                    );
                }
                return '-';
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true
        }
    ]), [navigate]);

    return (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row className="mb-3">
                        <Col md="3">
                            <FormGroup>
                                <Label>Başvuru Tarihi</Label>
                                <Input
                                    type="date"
                                    value={applicationDateSearch}
                                    onChange={handleApplicationDateChange}
                                    placeholder="Başvuru tarihi seçin..."
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Plaka Kaza Tarihi</Label>
                                <Input
                                    type="date"
                                    value={accidentDateSearch}
                                    onChange={handleAccidentDateChange}
                                    placeholder="Kaza tarihi seçin..."
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>TC Kimlik Numarası / Vergi Numarası ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="TC kimlik numarası / Vergi numarası..."
                                    value={tcIdSearch}
                                    onChange={handleTcIdChange}
                                    maxLength="11"
                                />
                                {tcIdSearch && tcIdSearch.trim().length > 0 && tcIdSearch.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Kapanma Açıklaması ile Ara</Label>
                                <Input type="select" value={closeCauseSearch} onChange={handleCloseCauseChange}>
                                    <option value="">Kapanma açıklaması seçin...</option>
                                    <option value="Gelen ödeme yeterli">Gelen ödeme yeterli</option>
                                    <option value="Aynı yerden eski hasarlı">Aynı yerden eski hasarlı</option>
                                    <option value="Ağır hasarlı">Ağır hasarlı</option>
                                    <option value="Farklı vekil ödeme almış">Farklı vekil ödeme almış</option>
                                    <option value="Farklı vekil tahkime başvuru yapmış">Farklı vekil tahkime başvuru yapmış</option>
                                    <option value="Vekalet süresi bitti">Vekalet süresi bitti</option>
                                    <option value="Azil">Azil</option>
                                    <option value="Teminat limiti dolu">Teminat limiti dolu</option>
                                    <option value="Sulh">Sulh</option>
                                    <option value="Geri çekme">Geri çekme</option>
                                    <option value="Feragat">Feragat</option>
                                    <option value="Bilirkişi ücreti yatırılmadı">Bilirkişi ücreti yatırılmadı</option>
                                    <option value="Kusur değişmedi">Kusur değişmedi</option>
                                    <option value="Bakiye limit çıkmadı">Bakiye limit çıkmadı</option>
                                    <option value="Görevsizlik">Görevsizlik</option>
                                    <option value="Kısmi dava açıldığından">Kısmi dava açıldığından</option>
                                    <option value="Fatura olmadığından">Fatura olmadığından</option>
                                    <option value="Derdestlik">Derdestlik</option>
                                    <option value="Zaman aşımı">Zaman aşımı</option>
                                    <option value="Yanlış başvuru">Yanlış başvuru</option>
                                    <option value="Diğer">Diğer</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <DataTable
                        columns={columns}
                        data={items}
                        progressPending={loading}
                        pagination
                        paginationServer
                        stickyActionPosition="right"
                        paginationTotalRows={totalRows}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        paginationPerPage={pageSize}
                        paginationDefaultPage={page}
                        selectableRows={false}
                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                        noDataComponent={loading ? null : "Kapalı dosya bulunamadı"}
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default TodoTabClosedFiles;
